import {Component, Injectable, OnDestroy, OnInit} from '@angular/core';
import {NgbActiveModal, NgbModal} from "@ng-bootstrap/ng-bootstrap";

import {ModalBase} from "../../../services/modal/modal-base";

import {CloudApiService} from "../../../services/cloud-api.service";
import {LibraryGroupDto} from "../../../services/dtos/library-group.dto";
import {UserSessionService} from "../../../services/user-session.service";
import {FormBuilder} from "@angular/forms";
import {UserDto} from "../../../services/dtos/user.dto";
import {UserGroupRoleDto} from "../../../services/dtos/user-group-role.dto";
import {Role} from "../../../services/dtos/enums/role.enum";
import {Subscription} from "rxjs";
import {Object} from "core-js";

enum State {
    None,
    Loading,
    Ready
}

@Component({
    selector: 'user-management-modal',
    template: require('./user-management.modal.component.html'),
    styles: [ require('./user-management.modal.component.css') ]
})
export class UserManagementModalComponent implements OnInit, OnDestroy {

    states = State;
    state: State = State.None;
    errorMessage: string;
    successMessage: string;
    canSave: boolean;

    formData: any;
    formTitle: string;

    userDto: UserDto = new UserDto();
    isGroupUser: boolean;

    id: number;
    emailAddress: string;
    description: string;
    isRoleReporting: boolean;
    isRoleUserManagement: boolean;
    isRoleApplicationSetup: boolean;
    selectedLibrary: LibraryGroupDto;
    private librarySubscription: Subscription;

    resetLinks: string[] = [];
    isResetLinkOpen : boolean = false;

    constructor(
        public activeModal: NgbActiveModal,
        private userSession: UserSessionService,
        private cloudApiService: CloudApiService,
        private formBuilder: FormBuilder
    ) {}

    ngOnInit(): void {
        this.checkForm();

        this.formData = this.formBuilder.group({
            emailAddress: this.emailAddress,
            description: this.description,
            roleReporting: this.isRoleReporting,
            roleApplicationSetup: this.isRoleApplicationSetup,
            roleUserManagement: this.isRoleUserManagement,
            id: this.id
        });

        this.state = State.Ready;

        this.librarySubscription = this.userSession.library.subscribe(library => {
            this.selectedLibrary = library;
        });
    }

    public ngOnDestroy(): void {
        this.librarySubscription.unsubscribe();
    }

    dismiss(): void {
        this.successMessage || this.errorMessage ?
            this.activeModal.close() : this.activeModal.dismiss();
    }

    save(formData: any): void {
        try{
            this.updateDto(formData);
            this.validate();

            if (this.canSave && this.id != 0) {
                this.cloudApiService.editUser(this.userDto).subscribe(
                    user => {
                        this.successMessage = 'User details updated successfully.'
                    },
                    error => {
                        this.errorMessage = error;
                    })
            }

            if (this.canSave && this.id == 0) {
                this.cloudApiService.createUser(this.userDto).subscribe(
                    user => {
                        this.successMessage = 'New user created successfully.'
                        this.userDto = user;
                    },
                    error => {
                        this.errorMessage = error;
                    })
            }
        }catch (Error) {
            console.log(Error);
            this.successMessage = null;
            this.errorMessage = Error.message;
        }
    }
    
    copyToClipboard(url: string): void{
        let txtArea = document.createElement("textarea");
        txtArea.id = 'txt';
        txtArea.style.position = 'fixed';
        txtArea.style.top = '0';
        txtArea.style.left = '0';
        txtArea.style.opacity = '0';
        txtArea.value = url;
        document.body.appendChild(txtArea);
        txtArea.select();

        let successful = document.execCommand('copy');
        document.body.removeChild(txtArea);
        successful ? this.successMessage = 'Copied to clipboard!' : this.errorMessage = 'Unable to copy to clipboard!';
    }

    openLink(url: string): void{
        this.successMessage = 'Password Reset Link has been open on a new tab.';
        window.open(url, '_blank').focus();
        this.isResetLinkOpen = true;
    }

    createLink(user: UserDto): void {
        try{
            this.errorMessage = null;
            this.cloudApiService.createUserPasswordResetToken(user.id)
                .subscribe(
                    token => {
                        this.successMessage = 'Password reset link created.'
                        this.resetLinks[user.id] = location.origin + '/#/password-reset/' + token;
                    },
                    error => {
                        this.errorMessage = error;
                    }
                );
        }catch (Error) {
            this.errorMessage = Error.message;
        }

    }

    private checkForm(): void {
        this.formTitle = this.userDto ? 'Edit User' : 'Create User';
        this.id = this.userDto.id ? this.userDto.id : 0;
        this.emailAddress = this.userDto ? this.userDto.emailAddress : '';
        this.description = this.userDto ? this.userDto.description : '';
        this.isRoleReporting = this.userDto.groupRoles ? this.isRoleSelected(this.userDto.groupRoles, Role.Reporting) : true;
        this.isRoleUserManagement = this.userDto.groupRoles ? this.isRoleSelected(this.userDto.groupRoles, Role.UserManagement) : false;
        this.isRoleApplicationSetup = this.userDto.groupRoles ? this.isRoleSelected(this.userDto.groupRoles, Role.ApplicationSetup) : false;
    }

    private isRoleSelected(groupRoles: UserGroupRoleDto[], role: Role): boolean{
        return groupRoles.some(r => r.role == role);
    }

    private updateDto(formData: any): void {
        this.userDto.id = formData.get('id').value;
        this.userDto.emailAddress = formData.get('emailAddress').value;
        this.userDto.description = formData.get('description').value;

        this.userDto.groupRoles = [];

        if (formData.get('roleReporting').value) {
            this.userDto.groupRoles.push(Object.assign(new UserGroupRoleDto(), {
                libraryGroupId: this.selectedLibrary.id,
                role: Role.Reporting,
            }));
        }

        if (formData.get('roleUserManagement').value) {
            this.userDto.groupRoles.push(Object.assign(new UserGroupRoleDto(), {
                libraryGroupId: this.selectedLibrary.id,
                role: Role.UserManagement,
            }));
        }

        if (formData.get('roleApplicationSetup').value) {
            this.userDto.groupRoles.push(Object.assign(new UserGroupRoleDto(), {
                libraryGroupId: this.selectedLibrary.id,
                role: Role.ApplicationSetup,
            }));
        }
    }
    checkLibraryConnected(user: UserDto): boolean{
        let x: number[] = [];
        if (Object.keys(user).length > 0){
            user.groupRoles.forEach(y => x.push(y.libraryGroupId));
            // filter with distinct value by its array
            x = x.filter((v, i, a) => a.indexOf(v) === i);
            x.length > 1 ? this.errorMessage = 'Please contact FE support team for support.' : '';
        }
        return x.length > 1 ? true : false;
    }

    validate(): void {
        this.errorMessage = null;

        if (this.userDto.groupRoles.length == 0)
            this.errorMessage = "At least one role must be selected";

        if (typeof this.userDto.description == 'undefined' || this.userDto.description.trim() == '')
            this.errorMessage = "Description required";

        let emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailRegex.test(String(this.userDto.emailAddress).toLowerCase()))
            this.errorMessage = "Invalid email address";

        this.errorMessage ? this.canSave = false : this.canSave = true;
    }
}

@Injectable()
export class UserManagementModal extends ModalBase {
    constructor(modalService: NgbModal) {
        super(modalService, UserManagementModalComponent)
    } 
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./src/app/components/modals/user-management/user-management.modal.component.ts